import { useMemo } from 'react';

import { Stack, Typography } from '@mui/material';

import { Markdown } from '@modules/_shared/components/Markdown';
import { WarningAlert } from '@modules/_shared/components/WarningAlert';
import { useLocale } from '@shared/hooks/locale';

type Error = {
  path: string[];
};

type AlertPropertyAppraisableProps = {
  errors: Error[];
  isPropertyTypeMissing?: boolean;
};

const AlertPropertyAppraisable = ({
  errors,
  isPropertyTypeMissing = false,
}: AlertPropertyAppraisableProps) => {
  const { t } = useLocale();

  const errorMappings = useMemo(
    () => ({
      type: t('Property type'),
      construction_year: t('buildYear'),
      land_surface: t('Land surface'),
      built_surface: t('Built surface'),
      living_surface: t('Living surface'),
      number_of_rooms: t('Number of rooms'),
      number_of_bedrooms: t('Number of bedrooms'),
      residential_surface: t('Residential surface'),
      residential_yearly_rental_income: t('actualRentalIncomeResidential'),
      number_of_indoor_parkings: t('indoorParking'),
      number_of_outdoor_parkings: t('outdoorParking'),
      number_of_residential_units: t('numberOfResidentialUnits'),
      number_of_commercial_units: t('numberOfCommercialUnits'),
      commercial_surface: t('commercialSurface'),
      indoor_parking_surface: t('indoorParking'),
      outdoor_parking_surface: t('outdoorParking'),
      commercial_yearly_rental_income: t('actualRentalIncomeCommercial'),
      parking_yearly_rental_income: t('actualRentalIncomeParking'),
      parking_potential_rental_income: t('potentialRentalIncomeParking'),
      commercial_potential_rental_income: t('potentialRentalIncomeCommercial'),
      residential_potential_rental_income: t(
        'potentialRentalIncomeResidential',
      ),
    }),
    [t],
  );

  const isTypeError =
    !isPropertyTypeMissing &&
    errors.some(error => error.path.some(pathPart => pathPart === 'type'));

  const mappedErrorPaths = errors.flatMap(e =>
    e.path.map(p => errorMappings[p as keyof typeof errorMappings]),
  );

  return (
    <WarningAlert
      title={
        isTypeError ? (
          t('propertyTypeNotAppraisable')
        ) : (
          <Markdown>{t('thisPropertyIsNotAvailableForAppraising')}</Markdown>
        )
      }
    >
      {!isTypeError && (
        <Stack
          sx={{
            gap: 0.25,
            mt: 0.75,
          }}
        >
          {mappedErrorPaths.map((pathPart, index) => (
            <Typography key={`${pathPart}-${index}`} variant="body2">
              • {pathPart}
            </Typography>
          ))}
        </Stack>
      )}
    </WarningAlert>
  );
};

export default AlertPropertyAppraisable;
